import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

import { IconButton } from "./IconButton";

export function Modal({ children, label, onClose, open, restoreFocusRef }: {
  children: ReactNode;
  label: string;
  onClose(): void;
  open: boolean;
  restoreFocusRef: RefObject<HTMLElement | null>;
}) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const root = document.getElementById("root");
    const previousInert = root?.inert ?? false;
    const previousOverflow = document.body.style.overflow;
    if (root) root.inert = true;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", escape);
    return () => {
      if (root) root.inert = previousInert;
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", escape);
      requestAnimationFrame(() => restoreFocusRef.current?.focus());
    };
  }, [open, restoreFocusRef]);

  function trapFocus(event: KeyboardEvent<HTMLElement>) {
    if (event.key !== "Tab") return;
    const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(
      "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex='-1'])",
    ));
    const first = controls[0];
    const last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  if (!open) return null;
  return createPortal(
    <div className="sbx-app sbx-overlay-root"><div className="modal-layer">
      <button className="modal-scrim" type="button" aria-label={`Dismiss ${label}`} onClick={onClose} />
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={trapFocus}>
        <header className="modal-header">
          <h2 id={titleId}>{label}</h2>
          <IconButton ref={closeRef} aria-label={`Close ${label}`} onClick={onClose}><span aria-hidden="true">×</span></IconButton>
        </header>
        {children}
      </section>
    </div></div>,
    document.body,
  );
}
